import { ILogonDocument } from 'app/services/api5-service/api.interface';

import { DocHelper } from 'utils/dochelper';
import { RequiredField } from 'utils/required-field.type';

/**
 * Append re-captcha metadata to loginDoc
 *
 * @param loginDoc - doc for login form
 */
function appendRecaptchaField(loginDoc: ILogonDocument): void {
  if (!loginDoc.metadata?.form) {
    return;
  }

  if (loginDoc.recaptcha_field) {
    if (!loginDoc.metadata.form.field) {
      loginDoc.metadata.form.field = [];
    }
    loginDoc.metadata.form.field.push({
      $name: 'recaptcha_field',
      $noname: 'yes',
      captcha: {
        $name: 'recaptcha_field',
      },
    });
  }
}

/**
 * Append select model to loginDoc
 *
 * @param loginDoc - doc for login form
 */
function appendLangSelectModel(
  loginDoc: RequiredField<ILogonDocument, 'loginform'>,
): void {
  if (loginDoc.loginform.lang.length > 0) {
    if (!loginDoc.slist) {
      loginDoc.slist = [];
    }
    loginDoc.slist.push({
      $name: 'lang',
      val: loginDoc.loginform.lang.map(langValue => ({
        $: DocHelper.getMessage(langValue.$, loginDoc),
        $key: langValue.$,
        $sprite: 'no',
        $spritesvg: 'no',
      })),
    });
    (loginDoc as any).lang = {
      $: loginDoc.loginform.$lang,
    };
  }
}
/**
 * Append password model to loginDoc
 *
 * @param loginDoc - doc for login form
 */
function appendPasswordModel(
  loginDoc: RequiredField<ILogonDocument, 'loginform'>,
): void {
  (loginDoc as any).password = {
    $: loginDoc.loginform.$pwd,
  };
}

/**
 * Append username model to loginDoc
 *
 * @param loginDoc - doc for login form
 */
function appendUsernameModel(
  loginDoc: RequiredField<ILogonDocument, 'loginform'>,
): void {
  (loginDoc as any).username = {
    $: loginDoc.loginform.$user,
  };
}

/**
 * Append hidden models to loginDoc
 *
 * @param loginDoc - doc for login form
 */
function appendHiddenModels(
  loginDoc: RequiredField<ILogonDocument, 'loginform'>,
): void {
  if (!loginDoc.metadata?.form) {
    return;
  }
  const loginForm = loginDoc.loginform;
  if (!loginDoc.metadata.form.field) {
    loginDoc.metadata.form.field = [];
  }
  if (loginForm.$project) {
    loginDoc.metadata.form.field.push({
      $name: 'project',
      input: [
        {
          $name: 'project',
          $type: 'hidden',
        },
      ],
    });
    (loginDoc as any).project = loginForm.$project;
  }
  if (loginForm.$welcomfunc) {
    loginDoc.metadata.form.field.push(
      {
        $name: 'welcomfunc',
        input: [
          {
            $name: 'welcomfunc',
            $type: 'hidden',
          },
        ],
      },
      {
        $name: 'welcomparam',
        input: [
          {
            $name: 'welcomparam',
            $type: 'hidden',
          },
        ],
      },
    );
    (loginDoc as any).welcomfunc = loginForm.$welcomfunc;
    (loginDoc as any).welcomparam = loginForm.$welcomparam;
  }
  if (loginDoc.tparams.sform) {
    loginDoc.metadata.form.field.push({
      $name: 'sfrom',
      input: [
        {
          $name: 'sfrom',
          $type: 'hidden',
        },
      ],
    });
    (loginDoc as any).sform = loginDoc.tparams.sform;
  }
}

/**
 * Map login specific doc to IFormDocument for dynamic-form
 *
 * @param loginDoc - Specific login form doc
 */
export function mapLoginDocToFormDoc(loginDoc: ILogonDocument): ILogonDocument {
  const loginDocWithLoginForm = loginDoc as RequiredField<
    ILogonDocument,
    'loginform'
  >;
  appendUsernameModel(loginDocWithLoginForm);
  appendPasswordModel(loginDocWithLoginForm);
  appendLangSelectModel(loginDocWithLoginForm);
  appendHiddenModels(loginDocWithLoginForm);
  appendRecaptchaField(loginDocWithLoginForm);

  // @HACK append type in order to pass check of empty metadata in DocHelper.isHaveMetadata
  if (loginDoc.metadata) {
    loginDoc.metadata.$type = 'form';
  }

  if (loginDoc.error?.$object === 'badpassword') {
    // make this error global
    loginDoc.error.$object = '';
  }

  return loginDoc;
}
